// src/components/family/profile/CareTeamPreviewCard.tsx
// Care team quick preview card for child profile overview

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  ChevronRight,
  Phone,
  Mail,
  Star,
  Building2,
  UserPlus,
  Stethoscope,
  GraduationCap,
  Scale,
  Heart,
} from 'lucide-react';
import type { CareTeamMember } from '@/types';

interface CareTeamPreviewCardProps {
  careTeam: CareTeamMember[];
  childId: string;
  isLoading?: boolean;
  className?: string;
}

// Provider type icons and colors
const providerConfig: Record<string, { icon: typeof Stethoscope; color: string; bgColor: string }> = {
  medical: {
    icon: Stethoscope,
    color: 'text-sage',
    bgColor: 'bg-sage/10',
  },
  therapy: {
    icon: Heart,
    color: 'text-terracotta',
    bgColor: 'bg-terracotta/10',
  },
  education: {
    icon: GraduationCap,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
  },
  legal: {
    icon: Scale,
    color: 'text-navy',
    bgColor: 'bg-navy/10',
  },
  other: {
    icon: Users,
    color: 'text-warm-gray',
    bgColor: 'bg-cream-dark',
  },
};

// Get initials from name
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

// Avatar gradient colors
const avatarGradients = [
  'from-sage to-sage-dark',
  'from-terracotta to-terracotta-light',
  'from-navy to-navy-light',
  'from-purple-400 to-purple-600',
  'from-blue-400 to-blue-600',
  'from-pink-400 to-pink-600',
];

const getAvatarGradient = (name: string): string => {
  const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return avatarGradients[charSum % avatarGradients.length];
};

const CareTeamMemberItem: React.FC<{ member: CareTeamMember }> = ({ member }) => {
  const config = providerConfig[member.memberType?.category || 'other'] || providerConfig.other;
  const initials = getInitials(member.name);
  const gradient = getAvatarGradient(member.name);

  return (
    <div className="flex items-center gap-3 py-3 px-4 -mx-4 hover:bg-cream/50 transition-colors group cursor-pointer">
      {/* Avatar */}
      <div className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
        <span className="text-sm font-display font-semibold text-white">
          {initials}
        </span>
        {member.isPrimary && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center ring-2 ring-white">
            <Star className="w-2.5 h-2.5 text-white" />
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-navy truncate group-hover:text-sage-dark transition-colors">
            {member.name}
          </h4>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-sm text-warm-gray">
          <span className="truncate">{member.title || member.memberType?.name || 'Provider'}</span>
          {member.organization && (
            <>
              <span className="text-warm-gray/50">•</span>
              <span className="truncate">{member.organization}</span>
            </>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {member.phone && (
          <a
            href={`tel:${member.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-lg bg-sage/10 flex items-center justify-center hover:bg-sage/20 transition-colors"
            aria-label={`Call ${member.name}`}
          >
            <Phone className="w-4 h-4 text-sage" />
          </a>
        )}
        {member.email && (
          <a
            href={`mailto:${member.email}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center hover:bg-navy/20 transition-colors"
            aria-label={`Email ${member.name}`}
          >
            <Mail className="w-4 h-4 text-navy" />
          </a>
        )}
      </div>
    </div>
  );
};

export const CareTeamPreviewCard: React.FC<CareTeamPreviewCardProps> = ({
  careTeam,
  childId,
  isLoading = false,
  className = '',
}) => {
  if (isLoading) {
    return <CareTeamPreviewCardSkeleton />;
  }

  // Sort to show primary members first
  const sortedCareTeam = [...careTeam].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });

  // Group by category for summary
  const categoryCounts = careTeam.reduce((acc, member) => {
    const category = member.memberType?.category || 'other';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className={`bg-white rounded-2xl border border-cream-dark ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <Users className="w-5 h-5 text-sage" />
          Care Team
          {careTeam.length > 0 && (
            <span className="text-sm font-normal text-warm-gray">
              ({careTeam.length})
            </span>
          )}
        </h3>
        <Link
          to={`/family/children/${childId}/care-team`}
          className="text-sm text-sage font-medium hover:text-sage-dark flex items-center gap-1 transition-colors"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Category summary pills */}
      {careTeam.length > 0 && (
        <div className="px-4 py-2 border-b border-cream-dark flex flex-wrap gap-2">
          {Object.entries(categoryCounts).map(([category, count]) => {
            const config = providerConfig[category] || providerConfig.other;
            const Icon = config.icon;
            return (
              <span
                key={category}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {count} {category}
              </span>
            );
          })}
        </div>
      )}

      {/* Team members list */}
      <div className="px-4 py-2 divide-y divide-cream-dark/50">
        {sortedCareTeam.length > 0 ? (
          sortedCareTeam.slice(0, 4).map((member) => (
            <CareTeamMemberItem key={member.id} member={member} />
          ))
        ) : (
          <EmptyCareTeamState childId={childId} />
        )}
      </div>

      {/* View more link */}
      {sortedCareTeam.length > 4 && (
        <div className="px-4 py-2 border-t border-cream-dark">
          <Link
            to={`/family/children/${childId}/care-team`}
            className="text-sm text-sage font-medium hover:text-sage-dark transition-colors"
          >
            View {sortedCareTeam.length - 4} more provider
            {sortedCareTeam.length - 4 !== 1 ? 's' : ''}
          </Link>
        </div>
      )}

      {/* Add member CTA */}
      <div className="px-4 py-3 border-t border-cream-dark bg-cream/30">
        <Link
          to={`/family/children/${childId}/care-team/add`}
          className="flex items-center justify-center gap-2 w-full py-2 border-2 border-dashed border-cream-dark rounded-xl text-sm font-medium text-warm-gray hover:border-sage/40 hover:text-sage-dark transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Add Care Team Member
        </Link>
      </div>
    </div>
  );
};

// Empty state
const EmptyCareTeamState: React.FC<{ childId: string }> = ({ childId }) => (
  <div className="py-6 text-center">
    <div className="w-12 h-12 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-3">
      <Users className="w-6 h-6 text-warm-gray" />
    </div>
    <p className="text-sm font-medium text-navy mb-1">No care team members</p>
    <p className="text-sm text-warm-gray mb-3">
      Add doctors, therapists, and other providers
    </p>
    <Link
      to={`/family/children/${childId}/care-team/add`}
      className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium text-sm hover:bg-sage-dark transition-colors"
    >
      <UserPlus className="w-4 h-4" />
      Add Member
    </Link>
  </div>
);

// Loading skeleton
export const CareTeamPreviewCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl border border-cream-dark animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-cream-dark">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-cream-dark rounded" />
        <div className="h-6 bg-cream-dark rounded w-24" />
      </div>
      <div className="h-4 bg-cream-dark rounded w-16" />
    </div>

    <div className="px-4 py-2 border-b border-cream-dark flex gap-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-6 bg-cream-dark rounded-full w-20" />
      ))}
    </div>

    <div className="px-4 py-2 space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <div className="w-10 h-10 rounded-xl bg-cream-dark" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-cream-dark rounded w-32" />
            <div className="h-3 bg-cream-dark rounded w-48" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default CareTeamPreviewCard;
